import { useCallback, useState } from 'react';
import { Edit as EditIcon } from '@mui/icons-material';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import type { AllocationName } from 'api';
import { SecondaryNavigationSelector } from 'components/ui';
import { VERSION_NAME_PREFIX } from 'pages/ValuationsAllocation/common/constants/valuations/valuations';
import { VALUATION_MARKET_DATE_TEST_IDS } from 'pages/ValuationsAllocation/ValuationMarketDate.testIds';

interface ValuationVersionSelectorProps {
  disabled?: boolean;
  isViewOnly?: boolean;
  onOpenMarketDateDialog?: () => void;
  onOpenRenameDialog?: () => void;
  onVersionChange: (slug: string) => void;
  selectedVersion: AllocationName | undefined;
  versions: AllocationName[];
}

const getVersionLabel = (version: AllocationName) => `${VERSION_NAME_PREFIX} ${version.version} - ${version.name}`;

const ValuationVersionSelector = ({
  disabled,
  isViewOnly,
  onOpenMarketDateDialog,
  onOpenRenameDialog,
  onVersionChange,
  selectedVersion,
  versions,
}: ValuationVersionSelectorProps) => {
  const theme = useTheme();
  const brandColor = theme.palette.navigation.secondary.infoLabel.value.brand;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSelect = useCallback(
    (slug: string) => {
      handleClose();
      onVersionChange(slug);
    },
    [handleClose, onVersionChange]
  );

  const handleEditClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      handleClose();
      onOpenRenameDialog?.();
    },
    [handleClose, onOpenRenameDialog]
  );

  const handleEditMarketDate = useCallback(() => {
    handleClose();
    onOpenMarketDateDialog?.();
  }, [handleClose, onOpenMarketDateDialog]);

  const selectorValue = selectedVersion ? `${selectedVersion.version} - ${selectedVersion.name}` : '';

  return (
    <>
      <SecondaryNavigationSelector
        disabled={disabled}
        label="Version"
        onClick={handleOpen}
        value={selectorValue}
        sx={{
          '--secondary-navigation-selector-label-color': `${brandColor} !important`,
          '--secondary-navigation-selector-value-color': `${brandColor} !important`,
          '--secondary-navigation-selector-icon-color': `${brandColor} !important`,
        }}
      />
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {versions.map(version => {
          const isSelected = version.slug === selectedVersion?.slug;
          return (
            <MenuItem
              key={version.id ?? version.slug}
              selected={isSelected}
              onClick={() => handleSelect(version.slug ?? '')}
              sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              {getVersionLabel(version)}
              {isSelected && onOpenRenameDialog && (
                <IconButton size="small" onClick={handleEditClick} sx={{ ml: 1 }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </MenuItem>
          );
        })}
        {!isViewOnly && onOpenMarketDateDialog && [
          <Divider key="divider" />,
          <MenuItem
            key="edit-market-date"
            data-testid={VALUATION_MARKET_DATE_TEST_IDS.editMarketDateMenuItem}
            onClick={handleEditMarketDate}>
            Edit Market Date
          </MenuItem>,
        ]}
      </Menu>
    </>
  );
};

export default ValuationVersionSelector;
